import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, Bell, MessageSquare, Loader2 } from "lucide-react";

interface Props {
  userId: string;
}

const BOT_USERNAME = "fitawrarifx_bot";
const STORAGE_KEY = "tg_subscribe_prompt_dismissed";

const TelegramSubscribeModal = ({ userId }: Props) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      // Skip if user already dismissed for this account
      if (localStorage.getItem(`${STORAGE_KEY}:${userId}`) === "1") return;

      // Check if already linked
      const { data: link } = await supabase
        .from("user_telegram")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled || link) return;

      // Fetch / generate the link code
      setLoading(true);
      try {
        const { data } = await supabase.functions.invoke("telegram-link-code");
        if (cancelled) return;
        if (data?.linked) return; // race-safe
        if (data?.code) setCode(data.code);
        setOpen(true);
      } catch (e) {
        console.error("subscribe modal", e);
      } finally {
        setLoading(false);
      }
    };
    check();

    // Auto-close once user links from Telegram
    const channel = supabase
      .channel(`tg-modal-${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "user_telegram", filter: `user_id=eq.${userId}` },
        () => setOpen(false)
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const dismiss = () => {
    localStorage.setItem(`${STORAGE_KEY}:${userId}`, "1");
    setOpen(false);
  };

  const linkUrl = code ? `https://t.me/${BOT_USERNAME}?start=${code}` : `https://t.me/${BOT_USERNAME}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <Send className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Subscribe to our Telegram bot</DialogTitle>
          <DialogDescription className="text-center">
            Get instant lesson reminders, streak alerts, and ask questions directly to your mentors.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 text-sm">
            <Bell className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>Real-time notifications about new lessons & courses</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <MessageSquare className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>Chat with admins from inside Telegram</span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="w-full">
            <Button className="w-full" size="lg" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Open Telegram & Subscribe
            </Button>
          </a>
          <Button variant="ghost" size="sm" onClick={dismiss} className="w-full">
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TelegramSubscribeModal;
